'use client';

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Check, Bell, Sparkles, Filter, Clock, Plus, ArrowRight, Star, Tv, TrendingUp, X, Film, Square, CheckSquare } from 'lucide-react';
import { useStore, type ActiveFilter, type TVFilter, type MovieFilter } from '@/lib/store';
import { cn } from '@/lib/utils';

interface FilterSortDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FilterSortDialog({ open, onOpenChange }: FilterSortDialogProps) {
  const { activeFilters, toggleFilter, selectAllTV, selectAllMovies, clearAllFilters, watchlist, sortBy, setSortBy, setFilterType } = useStore();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['tv', 'movies']));
  const [activeTab, setActiveTab] = useState<'filter' | 'sort'>('filter');
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setDragOffset(0);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    if (open) {
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [open, onOpenChange]);

  // Helper functions
  const hasWatchProgress = (item: any) => {
    if (item.type !== 'tv') return false;
    if (item.watchedSeasons && item.watchedSeasons.length > 0) return true;
    if (item.watchedEpisodes) {
      const totalWatchedEpisodes = Object.values(item.watchedEpisodes).reduce(
        (sum: number, episodes: any) => sum + (episodes?.length || 0), 0
      );
      if (totalWatchedEpisodes > 0) return true;
    }
    return false;
  };

  const isWatching = (item: any) => {
    if (item.type !== 'tv' || !item.currentlyWatching) return false;
    if (item.totalSeasons && item.watchedSeasons.length >= item.totalSeasons) {
      return false;
    }
    return true;
  };

  const isComplete = (item: any) => {
    if (item.type !== 'tv') return false;
    if (item.totalSeasons && item.watchedSeasons && item.watchedSeasons.length >= item.totalSeasons) {
      return true;
    }
    return false;
  };

  const hasNewContent = (item: any) => {
    if (item.type !== 'tv') return false;
    return item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled';
  };

  const isRecentlyAdded = (item: any, days: number = 14) => {
    if (!item.addedAt) return false;
    try {
      const addedDate = new Date(item.addedAt);
      const now = new Date();
      const daysSinceAdded = (now.getTime() - addedDate.getTime()) / (1000 * 60 * 60 * 24);

      if (item.type === 'tv') {
        return daysSinceAdded <= days && !hasWatchProgress(item) && !item.currentlyWatching;
      } else if (item.type === 'movie') {
        return daysSinceAdded <= days && !item.watched;
      }
    } catch (error) {
      return false;
    }
    return false;
  };

  // Count items for each filter - using exclusive logic to match displayed counts
  // Each count excludes items that appear in higher-priority sections

  // Priority 1: Recently Added (no exclusions needed - highest priority for new unwatched items)
  const tvRecentlyAddedCount = watchlist.filter(item =>
    item.type === 'tv' && isRecentlyAdded(item) && !isWatching(item)
  ).length;

  // Priority 2: New Content (exclude Recently Added, Watching)
  const tvNewCount = watchlist.filter(item =>
    hasNewContent(item) && !isWatching(item) && !isRecentlyAdded(item)
  ).length;

  // Priority 3: Watching (no exclusions - this is a user-set status)
  const tvWatchingCount = watchlist.filter(item => isWatching(item)).length;

  // Priority 4: In Progress (exclude Watching, Recently Added, New Content)
  const tvInProgressCount = watchlist.filter(item =>
    item.type === 'tv' &&
    hasWatchProgress(item) &&
    !item.currentlyWatching &&
    !isComplete(item) &&
    !isWatching(item) &&
    !isRecentlyAdded(item) &&
    !hasNewContent(item)
  ).length;

  // Priority 5: Unwatched (exclude Watching, Recently Added, New Content)
  const tvUnwatchedCount = watchlist.filter(item =>
    item.type === 'tv' &&
    !hasWatchProgress(item) &&
    !item.currentlyWatching &&
    !isWatching(item) &&
    !isRecentlyAdded(item) &&
    !hasNewContent(item)
  ).length;

  // Priority 6: Complete (exclude Watching, Recently Added, New Content)
  const tvCompleteCount = watchlist.filter(item =>
    isComplete(item) &&
    !isWatching(item) &&
    !isRecentlyAdded(item) &&
    !hasNewContent(item)
  ).length;

  const movieRecentlyAddedCount = watchlist.filter(item => item.type === 'movie' && isRecentlyAdded(item)).length;
  const movieUnwatchedCount = watchlist.filter(item =>
    item.type === 'movie' && !item.watched && !isRecentlyAdded(item)
  ).length;
  const movieCompleteCount = watchlist.filter(item =>
    item.type === 'movie' && item.watched
  ).length;

  const tvFilters: TVFilter[] = ['tv-recently-added', 'tv-new', 'tv-watching', 'tv-in-progress', 'tv-unwatched', 'tv-complete'];
  const movieFilters: MovieFilter[] = ['movie-recently-added', 'movie-unwatched', 'movie-complete'];

  const allTVSelected = tvFilters.every(f => activeFilters.includes(f));
  const allMoviesSelected = movieFilters.every(f => activeFilters.includes(f));
  const someTVSelected = tvFilters.some(f => activeFilters.includes(f)) && !allTVSelected;
  const someMoviesSelected = movieFilters.some(f => activeFilters.includes(f)) && !allMoviesSelected;

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleGroupToggle = (groupType: 'tv' | 'movies') => {
    // Reset filterType to 'all' when using hierarchical filters
    setFilterType('all');

    if (groupType === 'tv') {
      if (allTVSelected) {
        const newFilters = activeFilters.filter(f => !tvFilters.includes(f as TVFilter));
        useStore.getState().setFilters(newFilters);
      } else {
        selectAllTV();
      }
    } else {
      if (allMoviesSelected) {
        const newFilters = activeFilters.filter(f => !movieFilters.includes(f as MovieFilter));
        useStore.getState().setFilters(newFilters);
      } else {
        selectAllMovies();
      }
    }
  };

  const filterOptions = [
    { id: 'tv-recently-added', label: 'Recently Added', count: tvRecentlyAddedCount, group: 'tv', icon: <Sparkles className="h-3.5 w-3.5" /> },
    { id: 'tv-new', label: 'New Content', count: tvNewCount, group: 'tv', icon: <Bell className="h-3.5 w-3.5" /> },
    { id: 'tv-watching', label: 'Watching', count: tvWatchingCount, group: 'tv' },
    { id: 'tv-in-progress', label: 'In Progress', count: tvInProgressCount, group: 'tv' },
    { id: 'tv-unwatched', label: 'Unwatched', count: tvUnwatchedCount, group: 'tv' },
    { id: 'tv-complete', label: 'Complete', count: tvCompleteCount, group: 'tv' },
    { id: 'movie-recently-added', label: 'Recently Added', count: movieRecentlyAddedCount, group: 'movies', icon: <Sparkles className="h-3.5 w-3.5" /> },
    { id: 'movie-unwatched', label: 'Unwatched', count: movieUnwatchedCount, group: 'movies' },
    { id: 'movie-complete', label: 'Complete', count: movieCompleteCount, group: 'movies' },
  ];

  const sortOptions = [
    { value: 'recently-updated', label: 'Recently Updated', icon: <Clock className="h-5 w-5 text-gray-400" /> },
    { value: 'recently-added', label: 'Recently Added', icon: <Plus className="h-5 w-5 text-gray-400" /> },
    { value: 'name-asc', label: 'Name (A-Z)', icon: <ArrowRight className="h-5 w-5 text-gray-400 rotate-[-90deg]" /> },
    { value: 'name-desc', label: 'Name (Z-A)', icon: <ArrowRight className="h-5 w-5 text-gray-400 rotate-90" /> },
    { value: 'rating-high', label: 'Rating (High-Low)', icon: <Star className="h-5 w-5 text-yellow-400" /> },
    { value: 'rating-low', label: 'Rating (Low-High)', icon: <Star className="h-5 w-5 text-gray-400" /> },
    { value: 'seasons-most', label: 'Seasons (Most)', icon: <Tv className="h-5 w-5 text-red-400" /> },
    { value: 'seasons-least', label: 'Seasons (Least)', icon: <Tv className="h-5 w-5 text-gray-400" /> },
    { value: 'personal-rank', label: 'Personal Rank', icon: <TrendingUp className="h-5 w-5 text-green-400" /> },
  ];

  // Drag handlers for mobile menu
  const handleDragStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
  };

  const handleDragMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const offset = Math.max(0, currentY - dragStartY);
    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (dragOffset > 100) {
      onOpenChange(false);
    }
    setDragOffset(0);
    setDragStartY(0);
  };

  // Scroll-to-dismiss handlers for content area
  const handleScrollDragStart = (e: React.TouchEvent, scrollContainer: HTMLElement) => {
    // Only start tracking if we're at the top of the scroll
    if (scrollContainer.scrollTop === 0) {
      setDragStartY(e.touches[0].clientY);
    }
  };

  const handleScrollDragMove = (e: React.TouchEvent, scrollContainer: HTMLElement) => {
    // Only track drag if we're at the top of the scroll
    if (scrollContainer.scrollTop === 0 && dragStartY > 0) {
      const touch = e.touches[0];
      const currentY = touch.clientY;
      const offset = currentY - dragStartY;

      // Only allow downward dragging (positive offset)
      if (offset > 0) {
        setIsDragging(true);
        setDragOffset(offset);
        // Prevent the default scroll behavior when dragging down
        e.preventDefault();
        e.stopPropagation();
      }
    } else {
      // Reset if we're scrolling in the list
      if (isDragging) {
        setIsDragging(false);
        setDragOffset(0);
      }
      // Prevent background scroll when scrolling inside the menu
      e.stopPropagation();
    }
  };

  const handleScrollDragEnd = () => {
    if (isDragging) {
      // Close menu if dragged down more than 100px
      if (dragOffset > 100) {
        onOpenChange(false);
      }
      // Reset
      setIsDragging(false);
      setDragOffset(0);
      setDragStartY(0);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={() => onOpenChange(false)}
      />

      {/* Bottom Sheet Menu */}
      <div
        className="fixed inset-x-0 bottom-0 z-50 animate-in slide-in-from-bottom duration-300"
        style={{
          transform: `translateY(${dragOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <div className="bg-black rounded-t-3xl ring-1 ring-white/10 shadow-2xl max-h-[80vh] overflow-hidden flex flex-col">
          {/* Handle bar — Draggable area */}
          <div
            className="flex justify-center pt-2.5 pb-2 cursor-grab active:cursor-grabbing flex-shrink-0"
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="w-12 h-1.5 bg-white/30 rounded-full" />
          </div>

          {/* Header with Tabs */}
          <div className="flex-shrink-0">
            <div className="px-6 pt-2 pb-3">
              <h3 className="text-xl font-semibold tracking-tight text-white">Filter &amp; Sort</h3>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-white/10">
              <button
                onClick={() => setActiveTab('filter')}
                className={cn(
                  "flex-1 px-6 py-3 text-sm font-semibold transition-colors relative",
                  activeTab === 'filter'
                    ? "text-white"
                    : "text-gray-500 hover:text-gray-300"
                )}
              >
                Filter
                {activeTab === 'filter' && (
                  <span className="absolute bottom-0 inset-x-6 h-0.5 bg-red-500 rounded-full" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('sort')}
                className={cn(
                  "flex-1 px-6 py-3 text-sm font-semibold transition-colors relative",
                  activeTab === 'sort'
                    ? "text-white"
                    : "text-gray-500 hover:text-gray-300"
                )}
              >
                Sort By
                {activeTab === 'sort' && (
                  <span className="absolute bottom-0 inset-x-6 h-0.5 bg-red-500 rounded-full" />
                )}
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div
            className="overflow-y-auto flex-1"
            onTouchStart={(e) => handleScrollDragStart(e, e.currentTarget)}
            onTouchMove={(e) => handleScrollDragMove(e, e.currentTarget)}
            onTouchEnd={handleScrollDragEnd}
          >
          {activeTab === 'filter' ? (
            <div className="p-2">
              {/* TV Shows Section */}
              <div className="border-b border-white/10">
                <button
                  onClick={() => toggleGroup('tv')}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-xl py-3.5 px-3 text-sm outline-none hover:bg-white/5 transition-colors text-white"
                >
                  <span className="flex h-5 w-5 items-center justify-center mr-2 text-gray-400">
                    {expandedGroups.has('tv') ? (
                      <ChevronDown className="h-5 w-5" />
                    ) : (
                      <ChevronRight className="h-5 w-5" />
                    )}
                  </span>
                  <Tv className="h-5 w-5 mr-3 text-red-400" />
                  <span className="flex-1 font-semibold text-base text-left">TV Shows</span>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleGroupToggle('tv');
                    }}
                    className={cn(
                      "cursor-pointer w-6 h-6 rounded-md ring-1 flex items-center justify-center transition-all",
                      allTVSelected
                        ? "bg-red-600 ring-red-600"
                        : someTVSelected
                          ? "bg-red-600/30 ring-red-500/60"
                          : "ring-white/20 hover:ring-white/40"
                    )}
                  >
                    {(allTVSelected || someTVSelected) && <Check className="h-4 w-4 text-white" />}
                  </div>
                </button>

                {expandedGroups.has('tv') && (
                  <div className="pb-2">
                    {filterOptions.filter(opt => opt.group === 'tv').map((option) => {
                      const isSelected = activeFilters.includes(option.id as ActiveFilter);
                      return (
                        <button
                          key={option.id}
                          onClick={() => {
                            setFilterType('all');
                            toggleFilter(option.id as ActiveFilter);
                          }}
                          className="relative flex w-full cursor-pointer select-none items-center rounded-xl py-2.5 pl-12 pr-3 text-sm outline-none hover:bg-white/5 transition-colors"
                        >
                          <div className="absolute left-3 flex h-5 w-5 items-center justify-center">
                            <div
                              className={cn(
                                "w-5 h-5 rounded-md ring-1 flex items-center justify-center transition-colors",
                                isSelected
                                  ? "bg-red-600 ring-red-600"
                                  : "ring-white/20"
                              )}
                            >
                              {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                            </div>
                          </div>
                          <span className={cn("flex-1 text-left", isSelected ? "text-white font-medium" : "text-gray-400")}>
                            {option.label}
                          </span>
                          {option.icon && <span className="ml-2 text-gray-500">{option.icon}</span>}
                          {option.count !== undefined && option.count > 0 && (
                            <span className={cn(
                              "text-[11px] font-medium px-2 py-0.5 rounded-full ml-2 ring-1",
                              isSelected ? "bg-red-600/15 ring-red-500/30 text-red-300" : "bg-white/5 ring-white/10 text-gray-500"
                            )}>
                              {option.count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Movies Section */}
              <div>
                <button
                  onClick={() => toggleGroup('movies')}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-xl py-3.5 px-3 text-sm outline-none hover:bg-white/5 transition-colors text-white"
                >
                  <span className="flex h-5 w-5 items-center justify-center mr-2 text-gray-400">
                    {expandedGroups.has('movies') ? (
                      <ChevronDown className="h-5 w-5" />
                    ) : (
                      <ChevronRight className="h-5 w-5" />
                    )}
                  </span>
                  <Film className="h-5 w-5 mr-3 text-red-400" />
                  <span className="flex-1 font-semibold text-base text-left">Movies</span>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleGroupToggle('movies');
                    }}
                    className={cn(
                      "cursor-pointer w-6 h-6 rounded-md ring-1 flex items-center justify-center transition-all",
                      allMoviesSelected
                        ? "bg-red-600 ring-red-600"
                        : someMoviesSelected
                          ? "bg-red-600/30 ring-red-500/60"
                          : "ring-white/20 hover:ring-white/40"
                    )}
                  >
                    {(allMoviesSelected || someMoviesSelected) && <Check className="h-4 w-4 text-white" />}
                  </div>
                </button>

                {expandedGroups.has('movies') && (
                  <div className="pb-2">
                    {filterOptions.filter(opt => opt.group === 'movies').map((option) => {
                      const isSelected = activeFilters.includes(option.id as ActiveFilter);
                      return (
                        <button
                          key={option.id}
                          onClick={() => {
                            setFilterType('all');
                            toggleFilter(option.id as ActiveFilter);
                          }}
                          className="relative flex w-full cursor-pointer select-none items-center rounded-xl py-2.5 pl-12 pr-3 text-sm outline-none hover:bg-white/5 transition-colors"
                        >
                          <div className="absolute left-3 flex h-5 w-5 items-center justify-center">
                            <div
                              className={cn(
                                "w-5 h-5 rounded-md ring-1 flex items-center justify-center transition-colors",
                                isSelected
                                  ? "bg-red-600 ring-red-600"
                                  : "ring-white/20"
                              )}
                            >
                              {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                            </div>
                          </div>
                          <span className={cn("flex-1 text-left", isSelected ? "text-white font-medium" : "text-gray-400")}>
                            {option.label}
                          </span>
                          {option.icon && <span className="ml-2 text-gray-500">{option.icon}</span>}
                          {option.count !== undefined && option.count > 0 && (
                            <span className={cn(
                              "text-[11px] font-medium px-2 py-0.5 rounded-full ml-2 ring-1",
                              isSelected ? "bg-red-600/15 ring-red-500/30 text-red-300" : "bg-white/5 ring-white/10 text-gray-500"
                            )}>
                              {option.count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Clear All */}
              {activeFilters.length > 0 && (
                <div className="border-t border-white/10 mt-2 pt-2 px-2">
                  <button
                    onClick={clearAllFilters}
                    className="w-full py-3 px-4 text-sm text-red-400 hover:bg-red-600/15 rounded-xl transition-colors font-semibold"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-2 space-y-0.5">
              {sortOptions.map((option) => {
                const isSelected = sortBy === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value as any)}
                    className={cn(
                      "relative flex w-full cursor-pointer select-none items-center rounded-xl py-3 px-3 text-sm outline-none transition-colors",
                      isSelected ? "bg-white/5" : "hover:bg-white/5"
                    )}
                  >
                    <span className="flex h-5 w-5 items-center justify-center mr-3 text-gray-500">
                      {option.icon}
                    </span>
                    <span className={cn("flex-1 text-left", isSelected ? "text-white font-medium" : "text-gray-400")}>
                      {option.label}
                    </span>
                    {isSelected && <Check className="h-4 w-4 text-red-500 ml-2" />}
                  </button>
                );
              })}
            </div>
          )}
          </div>

          {/* Done Button — cinematic white pill */}
          <div className="p-4 pt-3 pb-6 flex-shrink-0 border-t border-white/10">
            <button
              onClick={() => onOpenChange(false)}
              className="w-full py-3.5 rounded-full bg-white text-black text-base font-semibold hover:bg-gray-200 transition-colors shadow-lg"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
